import { useAuth } from "@/auth/AuthProvider";
import {
  canonicalAccountingLinkState,
  isAccountingLinkConnected,
  type BondAccountingLink,
  type BondAccountingLinkState,
} from "@/lib/brokerBonds";
import {
  useAccountingLinkState,
  type BondLinkPollOptions,
} from "@/lib/useBondLink";
import { CustomerLinkPanel } from "@/components/CustomerLinkPanel";

/**
 * The link the broker hands to their customer, who uses it to connect their
 * accounting system (BRK-S115 / PMS-S256 / SUR-S088).
 *
 * This is the link WITH a lifecycle: `state` decides what the card says, and
 * that decision lives here rather than in `CustomerLinkPanel` — which is what
 * lets the bank link share the rendering without being given a state it does
 * not have.
 *
 * AN OUTSTANDING ITEM, NOT A FAILURE. The order is COMPLETE at submit (SUR-S052
 * answer 5) and there is nothing the broker can do if the customer never
 * connects (answer 7) — so no error styling, no "incomplete" badge, no nag, no
 * timeout, no dismiss. The pre-Link state is not an error on this surface even
 * though discovery answers a metric read against an unlinked connection with a
 * 409: the broker never asks for a metric.
 *
 * ABSENT is a FACT, not a gap: `accountingLink` is missing — never null — when
 * the order's configuration declared no accounting step, which is a normal
 * outcome for a bond product that asks for no financials. This renders NOTHING
 * for it, not an empty card and not a "not applicable" row.
 */
export function AccountingLinkPanel({
  link,
  orderId,
  poll,
}: {
  link?: BondAccountingLink;
  /**
   * The order to re-read while the state can still move (BRK-S119). Every
   * surface that shows an order's link has the order in hand, so the card
   * refreshes wherever it renders: the same component, the same freshness.
   *
   * Optional because the panel's job does not depend on it: with no id it
   * renders the state it was handed and never refreshes, which is strictly what
   * it did before this story.
   */
  orderId?: string;
  /** Injected in tests; defaults to a real timer. */
  poll?: BondLinkPollOptions;
}) {
  const { authedRequest } = useAuth();
  // The freshest state, not the one this surface was rendered with. Hooks run
  // before the absence check below — an absent link watches nothing.
  const current = useAccountingLinkState(authedRequest, orderId, link, poll);
  if (!current) return null;

  // What the broker can actually hand over, which is a different question from
  // what `state` says. `unminted` always carries nulls, and `pending` MAY: the
  // contract types both ids nullable on every state. So the rendering keys off
  // the url's presence within the state's framing rather than off the state
  // alone — which is what stops a copy control from ever sitting over an empty
  // string, and stops "send this link" appearing beside no link.
  const url =
    current.linkUrl && current.linkUrl.trim() !== "" ? current.linkUrl : null;
  const words = accountingLinkWords(current.state, url !== null);

  return (
    <CustomerLinkPanel
      headingId="accounting-link-heading"
      // OUR words on it, never the vendor's (BRK-S322): the broker is
      // connecting accounting software, and which company runs the link
      // behind it is ours to know. The connected platform (QuickBooks,
      // Xero…) would be the customer's own and worth naming, but the
      // contract does not publish it yet — so the heading says what it can.
      heading={
        words.connected
          ? "Accounting software connected"
          : "Connect your accounting software"
      }
      status={{ label: words.status, connected: words.connected }}
      lead={words.lead}
      detail={words.detail}
      url={words.offerUrl ? url : null}
      copyLabel="Copy accounting link"
    />
  );
}

/** One state's words: the status line, the two lines under it, and whether the
 *  url is handed over at all. */
interface AccountingLinkWords {
  /** The short status line. DISTINCT per state — a broker who cannot tell two
   *  states apart has been told nothing. */
  status: string;
  /** Whether the status reads as connected (the quiet green indicator). */
  connected: boolean;
  lead: string;
  detail: string;
  /** Whether to offer the url. A url the state makes useless is worse than no
   *  url: the broker sends it, the customer follows it, and nothing works. */
  offerUrl: boolean;
}

/**
 * The whole state → copy mapping, in ONE exhaustive place (BRK-S119's rendering
 * half). Every member of the union gets a branch and the `never` check below
 * proves it, so "no state renders blank" is a compile-time property rather than
 * a test we remembered to write. An eighth member cannot be added upstream and
 * silently render as `pending` — the mirror would reject it first, and this
 * would not compile second.
 *
 * TWO INPUTS, NOT ONE. `state` says what is TRUE; `hasUrl` says what can be
 * HANDED OVER, and the contract types both ids nullable on every state
 * (BRK-S116). The url's presence therefore chooses within a state's framing,
 * never instead of it — which is what stops a copy control sitting over an
 * empty string, and stops a connected customer's broker losing the link they
 * saw minutes earlier.
 *
 * `linked` renders exactly as `syncing`, through `canonicalAccountingLinkState`.
 * They are the same fact in two vocabularies, and the fold lives in the lib so
 * the day discovery stops emitting `linked` nothing here changes at all.
 *
 * NONE OF THESE IS AN ERROR. The order is complete at submit (SUR-S052 answer 5)
 * and there is nothing the broker can do if a customer never connects (answer
 * 7), so no state gets error styling, an "incomplete" badge, a nag or a dismiss
 * — `needsReauth` and `disabled` included. What those two get instead is a
 * NEXT STEP, because they are the two states where doing nothing leaves the
 * underwriter without the data and only the broker is in a position to notice.
 */
function accountingLinkWords(
  state: BondAccountingLinkState,
  hasUrl: boolean,
): AccountingLinkWords {
  const canonical = canonicalAccountingLinkState(state);
  const connected = isAccountingLinkConnected(state);
  switch (canonical) {
    case "unminted":
    case "pending":
      // The two states that mean "not connected yet", told apart by whether
      // there is anything to send. `unminted` always carries nulls in practice,
      // and minting is BEST EFFORT upstream — it never fails a submit — so the
      // no-url case is a filed, numbered order with nothing to hand over YET.
      // The contract asks for exactly this: "render the state rather than an
      // empty panel". Neither is an error the broker can act on.
      return hasUrl
        ? {
            status: "Not connected",
            connected,
            lead: "Waiting for your customer to connect.",
            detail:
              "Send this link to your customer. They use it to connect their accounting system — nothing else is needed from you.",
            offerUrl: true,
          }
        : {
            status: "Not connected",
            connected,
            lead: "We’re still preparing this link.",
            detail:
              "Your bond request is submitted and complete. The link to send your customer isn’t ready yet — check back on this submission shortly.",
            offerUrl: false,
          };
    case "linked":
    case "syncing":
      // Connected, and still moving — which is why this is the state the panel
      // keeps refreshing out of. It says CONNECTED first: that is the news, and
      // the sync is a detail the broker does not act on.
      return {
        status: "Connected — syncing data",
        connected,
        lead: "Your customer has connected their accounting system.",
        detail:
          "Their data is still coming across. Nothing else is needed from you.",
        offerUrl: hasUrl,
      };
    case "connected":
      return {
        status: "Connected",
        connected,
        lead: "Your customer has connected their accounting system.",
        detail:
          "Nothing else is needed from you. The link stays here if they ever need to reconnect.",
        offerUrl: hasUrl,
      };
    case "needsReauth":
      // The one state with something for the broker to DO, so it says so — and
      // it RE-PRESENTS the link, because the customer redoing the connection is
      // the only way out of it and the broker is who hands it over. Telling
      // them to act while withholding the means would be the worse half of both
      // options.
      return {
        status: "Reconnection needed",
        connected,
        lead: "Your customer needs to reconnect their accounting system.",
        detail:
          "Their connection has lapsed. Send them this link again so they can reconnect it — nothing else is needed from you.",
        offerUrl: hasUrl,
      };
    case "disabled":
      // NO URL, deliberately, whatever the wire carries. This connection was
      // switched off; a link that cannot reconnect it is a broker sending their
      // customer somewhere that does not work, and then both of them waiting.
      // The way back is a person, so the card names one.
      return {
        status: "Connection disabled",
        connected,
        lead: "This accounting connection has been switched off.",
        detail:
          "Nothing is needed from you. Contact our surety team if your customer needs it switched back on.",
        offerUrl: false,
      };
    default: {
      // Unreachable: the union is closed and every member is answered above.
      // This is the compile-time proof of it — an added member fails HERE,
      // which is the whole reason no state can render blank.
      const exhaustive: never = canonical;
      return exhaustive;
    }
  }
}
